import { useEffect, useMemo, useRef } from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import { VariableType } from '@/proto/common';
import { getAncestorVariables } from '@/pages/Logiblocks/variables/variableUtils';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import { ADDED_CONTACT_ROLE_KEYS } from '../contactRoles';

export const SHIPMENT_ROLE_KEYS = new Set([
  'TRANSPORT_PROVIDER_PRIMARY_CONTACT',
  'ORIGIN_PRIMARY_CONTACT',
  'DESTINATION_PRIMARY_CONTACT',
  'CONTROLLING_OFFICE_PRIMARY_CONTACT',
  ...ADDED_CONTACT_ROLE_KEYS,
]);

interface ContactOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Filters contact options to hide shipment-role contacts when there's no
 * upstream shipment in the workflow graph. Also clears a selected shipment-role
 * contactId when the upstream shipment disappears.
 *
 * Transport provider primary contact gets an extra fallback: it is also shown
 * when an ancestor node exposes a `carrier_name` variable (e.g. a PCA var
 * produced by an upstream inbound-agent action).
 */
export function useShipmentContactFilter(
  nodeId: string,
  contacts: ContactOption[],
  contactId: string,
  onContactIdChange: (id: string) => void
): {
  hasUpstreamShipment: boolean;
  hasUpstreamCarrierName: boolean;
  visibleContacts: ContactOption[];
} {
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();

  const ancestorVariables = useMemo(
    () => getAncestorVariables(nodeId, nodes, edges),
    [nodeId, nodes, edges]
  );

  const hasUpstreamShipment = useMemo(
    () =>
      ancestorVariables.some((ancestor) =>
        ancestor.filteredOutputVariables.some(
          (output) => output.variable!.type === VariableType.SHIPMENT
        )
      ),
    [ancestorVariables]
  );

  const hasUpstreamCarrierName = useMemo(
    () =>
      ancestorVariables.some((ancestor) =>
        ancestor.filteredOutputVariables.some(
          (output) => output.variable!.name === 'carrier_name'
        )
      ),
    [ancestorVariables]
  );

  const visibleContacts = useMemo(() => {
    if (hasUpstreamShipment) return contacts;
    return contacts.filter((c) => {
      if (c.value === 'TRANSPORT_PROVIDER_PRIMARY_CONTACT')
        return hasUpstreamCarrierName;
      return !SHIPMENT_ROLE_KEYS.has(c.value);
    });
  }, [contacts, hasUpstreamShipment, hasUpstreamCarrierName]);

  const onContactIdChangeRef = useRef(onContactIdChange);
  useEffect(() => {
    onContactIdChangeRef.current = onContactIdChange;
  });

  useEffect(() => {
    if (!SHIPMENT_ROLE_KEYS.has(contactId)) return;
    const isTP = contactId === 'TRANSPORT_PROVIDER_PRIMARY_CONTACT';
    const shouldClear = isTP
      ? !hasUpstreamShipment && !hasUpstreamCarrierName
      : !hasUpstreamShipment;
    if (shouldClear) onContactIdChangeRef.current('');
  }, [hasUpstreamShipment, hasUpstreamCarrierName, contactId]);

  return { hasUpstreamShipment, hasUpstreamCarrierName, visibleContacts };
}
